import {
  normalizeRowOrders,
  recalculateDueDates,
  mapRowsToAddListPayload,
  createEmptyProjectPlanRow,
  mapProductProjectPlansToRows,
  isRowsDirtyComparedToTemplate,
  validateProjectPlanBeforeSubmit,
} from './utils';
import {
  Button,
  Dialog,
  useTheme,
  TextField,
  DialogTitle,
  Autocomplete,
  DialogContent,
  DialogActions,
  Grid2 as Grid,
  svgIconClasses,
} from '@mui/material';
import {
  GridRowId,
  GridValidRowModel,
  GridRowSelectionModel,
  GridRowOrderChangeParams,
} from '@mui/x-data-grid-premium';
import dayjs, { Dayjs } from 'dayjs';
import { Book } from 'pages/dashboard';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import { DatePicker } from '@mui/x-date-pickers';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import { useIntl, FormattedMessage } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { useCreateProjectPlanColumns } from './columns';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import { CreateProjectPlanRow, DirtySourceType } from './types';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { Project, ProductProjectPlanItem, User } from 'library/types';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { renderAutoCompleteLocaleText, renderDataGridLocaleText } from 'utils';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { DataGridBox, ZebraDataGrid, CustomToolbar, PermissionHandler, CustomNoRowsOverlay } from 'components';
import { STORAGE_KEY_ENUM, PROJECT_STEP_PRIORITY_TYPE_ENUM, PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM } from 'enums';

const ProjectPlanGenerate = () => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const dispatch = useAppDispatch();
  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [books, setBooks] = useState<Book[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [rows, setRows] = useState<CreateProjectPlanRow[]>([]);
  const [openDirtyDialog, setOpenDirtyDialog] = useState(false);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [pendingProduct, setPendingProduct] = useState<Project | null>(null);
  const [projectStartDate, setProjectStartDate] = useState<Dayjs | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Project | null>(null);
  const [pendingStartDate, setPendingStartDate] = useState<Dayjs | null>(null);
  const [dirtySourceType, setDirtySourceType] = useState<DirtySourceType | null>(null);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [initialTemplateRows, setInitialTemplateRows] = useState<CreateProjectPlanRow[]>([]);

  // translate functions
  const getProjectStepPriorityTypeTranslated = useCallback(
    (projectStepPriorityType: number) =>
      ({
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.nonUrgent',
          defaultMessage: 'Düşük',
        }),
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.urgent',
          defaultMessage: 'Orta',
        }),
        [PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT]: intl.formatMessage({
          id: 'enum.projectStepPriorityType.veryUrgent',
          defaultMessage: 'Yüksek',
        }),
      })[projectStepPriorityType] ?? '',
    [intl]
  );

  const PRIORITY_OPTIONS = useMemo(
    () => [
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
        label: getProjectStepPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT,
        label: getProjectStepPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT),
      },
      {
        value: PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT,
        label: getProjectStepPriorityTypeTranslated(PROJECT_STEP_PRIORITY_TYPE_ENUM.VERY_URGENT),
      },
    ],
    [getProjectStepPriorityTypeTranslated]
  );

  const getProjectStepResponsibleContactTypeTranslated = useCallback(
    (projectStepResponsibleContactType: number) =>
      ({
        [PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER]: intl.formatMessage({
          id: 'enum.projectStepResponsibleContactType.customer',
          defaultMessage: 'Müşteri',
        }),
        [PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON]: intl.formatMessage({
          id: 'enum.projectStepResponsibleContactType.idecon',
          defaultMessage: 'Idecon',
        }),
        [PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON]: intl.formatMessage({
          id: 'enum.projectStepResponsibleContactType.customerAndIdecon',
          defaultMessage: 'Müşteri / Idecon',
        }),
      })[projectStepResponsibleContactType] ?? '',
    [intl]
  );

  const RESPONSIBLE_CONTACT_OPTIONS = useMemo(
    () => [
      {
        value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER,
        label: getProjectStepResponsibleContactTypeTranslated(PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER),
      },
      {
        value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON,
        label: getProjectStepResponsibleContactTypeTranslated(PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON),
      },
      {
        value: PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON,
        label: getProjectStepResponsibleContactTypeTranslated(
          PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON
        ),
      },
    ],
    [getProjectStepResponsibleContactTypeTranslated]
  );

  const columns = useCreateProjectPlanColumns({
    theme,
    users,
    priorityOptions: PRIORITY_OPTIONS,
    responsibleContactOptions: RESPONSIBLE_CONTACT_OPTIONS,
    getProjectStepPriorityTypeTranslated,
  });

  const getBooks = async () => {
    try {
      const response = await apiService.getBooks();

      if (response.isSuccess) {
        const sortedData = [...response.data].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
        setBooks(sortedData);
      }
    } catch {}
  };

  const getUsers = async () => {
    try {
      const response = await apiService.getUsers();

      if (response.isSuccess) {
        setUsers(response.data);
      }
    } catch {}
  };

  const getProductProjectPlansByProductId = useCallback(async (projectId: number) => {
    try {
      setLoading(true);

      const response = await apiService.getProductProjectPlans({
        projectId,
      });

      if (response.isSuccess) {
        return response.data as ProductProjectPlanItem[];
      }

      return [];
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fillTemplateRows = useCallback(
    async (product: Project | null, startDateArg?: Dayjs | null) => {
      if (!product) {
        setRows([]);
        setInitialTemplateRows([]);
        return;
      }

      const activeStartDate = startDateArg ?? projectStartDate;
      const templateItems = await getProductProjectPlansByProductId(product.id);

      const bookProject = selectedBook?.projects?.find((p) => p.id === product.id);

      const mappedRows = mapProductProjectPlansToRows({
        items: templateItems,
        bookId: selectedBook?.id ?? null,
        projectId: product.id ?? null,
        projectStartDate: activeStartDate,
        defaultRelatedContactId: bookProject?.relatedContactId ?? null,
        defaultAlternativeRelatedContactId: bookProject?.alternativeRelatedContactId ?? null,
      });

      setRows(mappedRows);
      setInitialTemplateRows(mappedRows);
      setRowSelectionModel([]);
    },
    [getProductProjectPlansByProductId, projectStartDate, selectedBook]
  );

  const hasDirtyState = useMemo(() => {
    return isRowsDirtyComparedToTemplate({
      rows,
      templateRows: initialTemplateRows,
    });
  }, [rows, initialTemplateRows]);

  const handleProductChange = async (_: unknown, newValue: Project | null) => {
    if (selectedProduct?.id === newValue?.id) return;

    if (!rows.length || !hasDirtyState) {
      setSelectedProduct(newValue);
      await fillTemplateRows(newValue);
      return;
    }

    setPendingProduct(newValue);
    setDirtySourceType('product');
    setOpenDirtyDialog(true);
  };

  const handleProjectStartDateChange = async (newValue: Dayjs | null) => {
    const sameDate =
      (!projectStartDate && !newValue) || (projectStartDate && newValue && projectStartDate.isSame(newValue, 'day'));

    if (sameDate) return;

    if (!rows.length || !hasDirtyState) {
      setProjectStartDate(newValue);
      setRows((prev) => recalculateDueDates({ rows: prev, projectStartDate: newValue }));
      return;
    }

    setPendingStartDate(newValue);
    setDirtySourceType('startDate');
    setOpenDirtyDialog(true);
  };

  const processRowUpdate = (newRow: GridValidRowModel, _oldRow: GridValidRowModel, _params: { rowId: GridRowId }) => {
    const typedRow = newRow as CreateProjectPlanRow & { dueDate?: unknown };

    const normalizedDueDate =
      typedRow.dueDate === null || typedRow.dueDate === undefined || typedRow.dueDate === ''
        ? null
        : dayjs(typedRow.dueDate).isValid()
          ? dayjs(typedRow.dueDate).toISOString()
          : null;

    const updatedRow: CreateProjectPlanRow = {
      ...typedRow,
      bookId: selectedBook?.id ?? null,
      projectId: selectedProduct?.id ?? null,
      dueDate: normalizedDueDate,
      responsibleCustomerFullName:
        typedRow.responsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON
          ? ''
          : typedRow.responsibleCustomerFullName,
    };

    let nextRows = rows.map((row) => (row.tempId === updatedRow.tempId ? updatedRow : row));

    nextRows = recalculateDueDates({
      rows: nextRows,
      projectStartDate,
    });

    setRows(nextRows);

    return nextRows.find((row) => row.tempId === updatedRow.tempId) ?? updatedRow;
  };

  const handleAddRow = () => {
    const nextRow = createEmptyProjectPlanRow(
      selectedBook?.id ?? null,
      selectedProduct?.id ?? null,
      projectStartDate,
      rows.length + 1
    );

    const nextRows = [...rows, nextRow];
    setRows(
      recalculateDueDates({
        rows: nextRows,
        projectStartDate,
      })
    );
  };

  const handleDeleteRows = () => {
    if (!rowSelectionModel.length) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'checkbox.noSelection',
          defaultMessage: 'Lütfen en az 1 satır seçiniz!',
        }),
        { variant: 'warning' }
      );
      return;
    }

    const filteredRows = normalizeRowOrders(rows.filter((row) => !rowSelectionModel.includes(row.tempId)));

    setRows(
      recalculateDueDates({
        rows: filteredRows,
        projectStartDate,
      })
    );
    setRowSelectionModel([]);
  };

  const handleReloadTemplate = async () => {
    if (!selectedProduct) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'projectPlanCreate.selectProduct',
          defaultMessage: 'Lütfen ürün seçiniz!',
        }),
        { variant: 'warning' }
      );
      return;
    }

    await fillTemplateRows(selectedProduct);
  };

  const handleResetToTemplate = () => {
    if (!initialTemplateRows.length) {
      return;
    }

    setRows(initialTemplateRows.map((row) => ({ ...row })));
    setRowSelectionModel([]);
  };

  const handleConfirmDirtyAction = async () => {
    setOpenDirtyDialog(false);

    if (dirtySourceType === 'product') {
      const nextProduct = pendingProduct;
      setSelectedProduct(nextProduct);
      await fillTemplateRows(nextProduct);
      setPendingProduct(null);
    }

    if (dirtySourceType === 'startDate') {
      const nextStartDate = pendingStartDate;
      setProjectStartDate(nextStartDate);
      setRows((prev) => recalculateDueDates({ rows: prev, projectStartDate: nextStartDate }));
      setPendingStartDate(null);
    }

    setDirtySourceType(null);
  };

  const handleCancelDirtyAction = () => {
    setOpenDirtyDialog(false);
    setDirtySourceType(null);
    setPendingProduct(null);
    setPendingStartDate(null);
  };

  const handleSave = async () => {
    const validationError = validateProjectPlanBeforeSubmit({
      projectStartDate,
      bookId: selectedBook?.id ?? null,
      projectId: selectedProduct?.id ?? null,
      rows,
      language: intl.locale,
    });

    if (validationError) {
      enqueueSnackbar(validationError, { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);

      const normalizedRows = rows.map((row) => ({
        ...row,
        bookId: selectedBook?.id ?? null,
        projectId: selectedProduct?.id ?? null,
      }));

      const payload = mapRowsToAddListPayload(normalizedRows);

      const response = await apiService.createProjectSteps(payload);

      if (response.isSuccess) {
        enqueueSnackbar(
          intl.formatMessage({
            id: 'projectPlanCreate.saveSuccess',
            defaultMessage: 'Proje planı başarıyla kaydedildi.',
          }),
          { variant: 'success' }
        );

        setRows([]);
        setInitialTemplateRows([]);
        setRowSelectionModel([]);
        setProjectStartDate(null);
        setSelectedBook(null);
        setSelectedProduct(null);
        setPendingProduct(null);
        setPendingStartDate(null);
        setDirtySourceType(null);
        setOpenDirtyDialog(false);

        navigate('/project-step-managing');
      }
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const handleRowOrderChange = (params: GridRowOrderChangeParams) => {
    const oldIndex = rows.findIndex((row) => row.tempId === params.row.tempId);
    const newIndex = params.targetIndex;

    if (oldIndex === -1 || newIndex === -1) return;

    const cloned = [...rows];
    const [movedRow] = cloned.splice(oldIndex, 1);
    cloned.splice(newIndex, 0, movedRow);

    const normalizedRows = normalizeRowOrders(cloned);
    const recalculatedRows = recalculateDueDates({
      rows: normalizedRows,
      projectStartDate,
    });

    setRows(recalculatedRows);
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('add-project-task-list')) {
      navigate('/dashboard');
    }
  }, [navigate, permissionCodes]);

  useEffect(() => {
    getBooks();
    getUsers();
  }, []);

  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [dispatch, isEmptyProducts, products]);

  useEffect(() => {
    const nextBookId = selectedBook?.id ?? null;

    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        bookId: nextBookId,
      }))
    );

    setInitialTemplateRows((prev) =>
      prev.map((row) => ({
        ...row,
        bookId: nextBookId,
      }))
    );
  }, [selectedBook]);

  return (
    <>
      <Grid container spacing={1} mb={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <DatePicker
            label={intl.formatMessage({
              id: 'projectPlanCreate.projectStartDate',
              defaultMessage: 'Proje Başlangıç Tarihi',
            })}
            value={projectStartDate}
            onChange={handleProjectStartDateChange}
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Autocomplete
            fullWidth
            options={books}
            value={selectedBook}
            {...renderAutoCompleteLocaleText()}
            getOptionLabel={(option: Book) => option.companyName ?? ''}
            onChange={(_, newValue: Book | null) => {
              setSelectedBook(newValue);

              if (selectedProduct && newValue) {
                const isProductInBook = newValue.projects.some((p) => p.id === selectedProduct.id);
                if (!isProductInBook) {
                  setSelectedProduct(null);
                  setRows([]);
                  setInitialTemplateRows([]);
                }
              }

              if (!newValue) {
                setSelectedProduct(null);
                setRows([]);
                setInitialTemplateRows([]);
              }
            }}
            slotProps={{
              paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={intl.formatMessage({
                  id: 'projectStepManaging.book',
                  defaultMessage: 'Proje',
                })}
                sx={{
                  '& .MuiButtonBase-root': {
                    border: 'none',
                    backgroundColor: 'inherit',
                    width: '2.25rem',
                    height: '2.25rem',
                    padding: '0.25rem',
                    [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                    },
                  },
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Autocomplete
            fullWidth
            disabled={!selectedBook}
            options={(() => {
              if (!selectedBook) return [];

              const filteredProducts = products.filter((product) =>
                selectedBook.projects.some((bookProduct) => bookProduct.id === product.id)
              );
              return filteredProducts.sort((a, b) => a.title.localeCompare(b.title));
            })()}
            value={selectedProduct}
            {...renderAutoCompleteLocaleText()}
            getOptionLabel={(option: Project) => option.title ?? ''}
            onChange={handleProductChange}
            slotProps={{
              paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={intl.formatMessage({
                  id: 'project.table.product',
                  defaultMessage: 'Ürün',
                })}
                sx={{
                  '& .MuiButtonBase-root': {
                    border: 'none',
                    backgroundColor: 'inherit',
                    width: '2.25rem',
                    height: '2.25rem',
                    padding: '0.25rem',
                    [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                    },
                  },
                }}
              />
            )}
          />
        </Grid>
      </Grid>

      <Grid display="flex" justifyContent="space-between" container spacing={1} mb={2}>
        <PermissionHandler requiredPermissionCodes={['add-project-task-list']}>
          <Grid spacing={1} container>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="projectPlanCreate.addRow" defaultMessage="Satır Ekle" />
            </Button>

            <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleDeleteRows}>
              <FormattedMessage id="projectPlanCreate.deleteRow" defaultMessage="Satır Sil" />
            </Button>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<KeyboardReturnIcon />}
              onClick={handleResetToTemplate}>
              <FormattedMessage id="projectPlanCreate.resetToTemplate" defaultMessage="Şablona Geri Dön" />
            </Button>

            <Button
              variant="contained"
              color="warning"
              startIcon={<ReplayIcon />}
              onClick={handleReloadTemplate}
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(194, 148, 10, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="projectPlanCreate.reloadTemplate" defaultMessage="Şablonu Yeniden Yükle" />
            </Button>
          </Grid>

          <Grid spacing={1} container>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
              <FormattedMessage id="save" defaultMessage="Kaydet" />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>

      <DataGridBox>
        <ZebraDataGrid
          pagination
          checkboxSelection
          rows={rows}
          columns={columns}
          getRowId={(row) => row.tempId}
          loading={loading || saving}
          disableRowSelectionOnClick
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newModel) => setRowSelectionModel(newModel)}
          localeText={renderDataGridLocaleText()}
          processRowUpdate={processRowUpdate}
          rowReordering
          onRowOrderChange={handleRowOrderChange}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          slotProps={{
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
              },
            },
          }}
          disableRowGrouping
        />
      </DataGridBox>

      <Dialog open={openDirtyDialog} onClose={handleCancelDirtyAction}>
        <DialogTitle>
          <FormattedMessage id="projectPlanCreate.dirtyDialogTitle" defaultMessage="Değişiklikler sıfırlanacak" />
        </DialogTitle>

        <DialogContent>
          {dirtySourceType === 'product' ? (
            <FormattedMessage
              id="projectPlanCreate.dirtyDialogDescriptionProduct"
              defaultMessage="Seçili ürün değiştirildiğinde mevcut tablo verileri sıfırlanacaktır. Devam etmek istiyor musunuz?"
            />
          ) : (
            <FormattedMessage
              id="projectPlanCreate.dirtyDialogDescriptionStartDate"
              defaultMessage="Termin tarihleri yeniden hesaplanacaktır. Devam etmek istiyor musunuz?"
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancelDirtyAction}>
            <FormattedMessage id="cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" color="warning" onClick={handleConfirmDirtyAction}>
            <FormattedMessage id="continue" defaultMessage="Devam Et" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectPlanGenerate;
